import {Box, makeStyles, Tooltip, Typography} from '@material-ui/core'
import {MenuItem} from '@smartb/archetypes-ui-layout'
import {city} from 'plateform-project-domain'
import {city as cityStats} from 'plateform-project-stats-domain'
import React, {useContext} from 'react'
import {useTranslation} from 'react-i18next'
import {InfoBox} from '../../Component/InfoBox'
import {MoreOptions} from '../../Component/MoreOptions'
import {ProgressBar} from '../../Component/ProgressBar'
import {StatusTags, StatusTagsFormProps} from '../../Component/StatusTags'
import {PrimaryTypography, Table} from '../../Component/Table'
import {displayNumber} from '../../utils'
import {themeContext} from "@smartb/archetypes-ui-components";

interface Project extends city.colisactiv.project.model.Project {}
interface ProjectStats
    extends cityStats.colisactiv.plateform.project.stats.model.ProjectStats {}
interface ProjectFunder extends city.colisactiv.project.model.ProjectFunder {}

export interface ProjectRow {
  id: string
  name: string
  totalBudget: number
  consumedBudget: number
  estimatedBudget: number
  potentialBudget: number
  missingBudget: number
  activeDeliveries: number
  firstPercentage: number
  secondPercentage: number
  actions: MenuItem[]
  status: StatusTagsFormProps
  budgetCee: number
  budgetTerritory: number
  paidBonusAmountCee: number
  paidBonusAmountTerritory: number
  totalBonusAmountCee: number
  totalBonusAmountTerritory: number
  hasMixedBudgetFunding: boolean
  hasMixedBonusFunding: boolean
}

const useStyles = makeStyles(() => ({
  tooltip: {
    background: 'white',
    border: 'solid 1px #C5C7D0',
    color: '#353945'
  },
  arrow: {
    color: '#C5C7D0'
  },
  container: {
    '& .rdt_TableRow .rdt_TableCell:nth-child(2n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(2n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(3n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(3n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(4n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(4n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(5n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(5n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(6n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(6n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(7n)': {
      minWidth: '170px'
    },
    '& .rdt_TableCol:nth-child(7n)': {
      minWidth: '170px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(8n)': {
      minWidth: '140px'
    },
    '& .rdt_TableCol:nth-child(8n)': {
      minWidth: '140px'
    },
    '& .rdt_TableRow .rdt_TableCell:last-child': {
      minWidth: '80px',
      maxWidth: '80px'
    },
    '& .rdt_TableCol:last-child': {
      minWidth: '80px',
      maxWidth: '80px'
    },
    '& .rdt_TableRow .rdt_TableCell:first-child': {
      minWidth: '150px'
    },
    '& .rdt_TableCol:first-child': {
      minWidth: '150px'
    }
  }
}))

interface ProjectTableProps {
  projects: ProjectStats[]
  onRowClicked?: (row: ProjectRow) => void
  totalPages: number
  page: number
  handlePageChange: (page: number) => void
  isLoading?: boolean
  getActions?: (row: ProjectStats) => MenuItem[]
  getStatus: (row: ProjectStats) => StatusTagsFormProps
}

export const ProjectTable = (props: ProjectTableProps) => {
  const {
    projects,
    onRowClicked,
    handlePageChange,
    page,
    totalPages,
    isLoading = false,
    getActions = () => [],
    getStatus
  } = props
  const calculateTotalBudget = (projectFunders: ProjectFunder[]): number => {
    let total = 0
    projectFunders.forEach((element) => {
      total = total + element.budget
    })
    return total
  }
  const { t } = useTranslation()
  const data: ProjectRow[] = projects.map(
      (project): ProjectRow => {
        const consumedBudget = Math.round(project.paidBonusAmount)
        const estimatedBudget = Math.round(project.totalBonusAmount)
        const missingBudget = Math.round(project.missingBudget)
        const totalBudget = Math.round(calculateTotalBudget(project.funders))
        return {
          id: project.id,
          name: project.details.name,
          totalBudget: totalBudget,
          consumedBudget: consumedBudget,
          estimatedBudget: estimatedBudget,
          missingBudget: missingBudget,
          activeDeliveries: project.activeDeliveryCount,
          firstPercentage: totalBudget !== 0 ? (consumedBudget * 100) / totalBudget : 100,
          secondPercentage: totalBudget !== 0 ? (estimatedBudget * 100) / totalBudget : 100,
          actions: getActions(project),
          status: getStatus(project),
          budgetCee: project.budgetCee,
          budgetTerritory: project.budgetTerritory,
          paidBonusAmountCee: project.paidBonusAmountCee,
          paidBonusAmountTerritory: project.paidBonusAmountTerritory,
          totalBonusAmountCee: project.totalBonusAmountCee,
          totalBonusAmountTerritory: project.totalBonusAmountTerritory,
          hasMixedBudgetFunding: project.budgetCee > 0 && project.budgetTerritory > 0,
          hasMixedBonusFunding: project.totalBonusAmountCee > 0 && project.totalBonusAmountTerritory > 0
        }
      }
  )
  const classes = useStyles()
  const theme = useContext(themeContext)

  const columns = [
    {
      name: 'Nom',
      cell: (row: ProjectRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {row.name}
          </PrimaryTypography>
      )
    },
    {
      name: 'Budget total',
      cell: (row: ProjectRow) => {
        return (
            <Box display="flex" alignItems="center">
              <PrimaryTypography
                  data-tag='___react-data-table-allow-propagation___'
                  align='left'
              >{`${displayNumber(row.totalBudget)} €`}</PrimaryTypography>
              {row.hasMixedBudgetFunding && (
                  <InfoBox
                      style={{marginLeft: 4, width: 16, height: 16}}
                      content={
                        <Box>
                          <Typography variant="body2">CEE: {displayNumber(Math.round(row.budgetCee))} €</Typography>
                          <Typography variant="body2">Territoire: {displayNumber(Math.round(row.budgetTerritory))} €</Typography>
                        </Box>
                      }
                  />
              )}
            </Box>
        )
      }
    },
    {
      name: 'Budget payé',
      cell: (row: ProjectRow) => {
        return (
            <Box display="flex" alignItems="center">
              <PrimaryTypography
                  data-tag='___react-data-table-allow-propagation___'
                  align='left'
              >{`${displayNumber(row.consumedBudget)} €`}</PrimaryTypography>
              {row.hasMixedBudgetFunding && (
                  <InfoBox
                      style={{marginLeft: 4, width: 16, height: 16}}
                      content={
                        <Box>
                          <Typography variant="body2">CEE: {displayNumber(Math.round(row.paidBonusAmountCee))} €</Typography>
                          <Typography variant="body2">Territoire: {displayNumber(Math.round(row.paidBonusAmountTerritory))} €</Typography>
                        </Box>
                      }
                  />
              )}
            </Box>
        )
      }
    },
    {
      name: 'Budget engagé',
      cell: (row: ProjectRow) => {
        return (
            <Box display="flex" alignItems="center">
              <PrimaryTypography
                  data-tag='___react-data-table-allow-propagation___'
                  align='left'
              >
                {`${displayNumber(row.estimatedBudget)} €`}
              </PrimaryTypography>
              {row.hasMixedBudgetFunding && (
                  <InfoBox
                      style={{marginLeft: 4, width: 16, height: 16}}
                      content={
                        <Box>
                          <Typography variant="body2">CEE: {displayNumber(Math.round(row.totalBonusAmountCee))} €</Typography>
                          <Typography variant="body2">Territoire: {displayNumber(Math.round(row.totalBonusAmountTerritory))} €</Typography>
                        </Box>
                      }
                  />
              )}
            </Box>
        )
      }
    },
    {
      name: 'Budget manquant',
      cell: (row: ProjectRow) => {
        const missingCee = Math.max(0, Math.round(row.totalBonusAmountCee - row.budgetCee))
        const missingTerritory = Math.max(0, Math.round(row.totalBonusAmountTerritory - row.budgetTerritory))
        const hasOverBudget = missingCee > 0 || missingTerritory > 0
        return (
            <Box display="flex" alignItems="center">
              <Typography
                  data-tag='___react-data-table-allow-propagation___'
                  align='left'
                  style={{
                    color: hasOverBudget ? 'orange' : '#071017',
                    fontSize: '13px',
                    fontWeight: 600
                  }}
              >
                {`${displayNumber(row.missingBudget)} €`}
              </Typography>
              {row.hasMixedBudgetFunding && (
                  <InfoBox
                      style={{marginLeft: 4, width: 16, height: 16}}
                      content={
                        <Box>
                          <Typography variant="body2">CEE: {displayNumber(missingCee)} €</Typography>
                          <Typography variant="body2">Territoire: {displayNumber(missingTerritory)} €</Typography>
                        </Box>
                      }
                  />
              )}
            </Box>
        )
      }
    },
    {
      name: 'Livraisons actives',
      cell: (row: ProjectRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {displayNumber(row.activeDeliveries)}
          </PrimaryTypography>
      )
    },
    {
      name: 'Progression',
      cell: (row: ProjectRow) => {
        const paidCeePercent = row.budgetCee > 0 ? Math.round(row.paidBonusAmountCee / row.budgetCee * 100) : 0
        const paidTerritoryPercent = row.budgetTerritory > 0 ? Math.round(row.paidBonusAmountTerritory / row.budgetTerritory * 100) : 0
        const engagedCeePercent = row.budgetCee > 0 ? Math.round(row.totalBonusAmountCee / row.budgetCee * 100) : 0
        const engagedTerritoryPercent = row.budgetTerritory > 0 ? Math.round(row.totalBonusAmountTerritory / row.budgetTerritory * 100) : 0
        const ceeOverBudget = engagedCeePercent > 100
        const territoryOverBudget = engagedTerritoryPercent > 100

        const progressBar = (
            <ProgressBar
                withLabel
                firstPercent={Math.round(row.firstPercentage)}
                firstColor={theme.secondaryColor}
                secondPercent={Math.round(row.secondPercentage)}
                secondColor={Math.round(row.estimatedBudget) <= Math.round(row.totalBudget) ? theme.primaryColor : "red"}
            />
        )

        if (!row.hasMixedBudgetFunding) {
          return progressBar
        }

        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                arrow
                placement="top"
                title={
                  <Box style={{minWidth: 150}}>
                    <Typography variant="body2" style={{marginBottom: 4}}>CEE</Typography>
                    <ProgressBar
                        withLabel
                        firstPercent={paidCeePercent}
                        firstColor={theme.secondaryColor}
                        secondPercent={engagedCeePercent}
                        secondColor={ceeOverBudget ? "red" : theme.primaryColor}
                    />
                    <Typography variant="body2" style={{marginTop: 8, marginBottom: 4}}>Territoire</Typography>
                    <ProgressBar
                        withLabel
                        firstPercent={paidTerritoryPercent}
                        firstColor={theme.secondaryColor}
                        secondPercent={engagedTerritoryPercent}
                        secondColor={territoryOverBudget ? "red" : theme.primaryColor}
                    />
                  </Box>
                }
            >
              <div style={{width: '100%', borderBottom: '1px dotted #C5C7D0', cursor: 'pointer'}}>{progressBar}</div>
            </Tooltip>
        )
      }
    },
    {
      name: 'Statut',
      cell: (row: ProjectRow) => {
        const status = row.status
        return <StatusTags {...status} />
      }
    },
    {
      name: 'Action',
      cell: (row: ProjectRow) => (
          <MoreOptions handleClose={() => {}} options={row.actions} />
      )
    }
  ]
  return (
      <Table<ProjectRow>
          data={data}
          className={classes.container}
          columns={columns}
          onRowClicked={onRowClicked}
          handlePageChange={handlePageChange}
          totalPages={totalPages}
          page={page}
          isLoading={isLoading}
          noDataMessage={t('app_no_data_project_table')}
      />
  )
}
